import styled, { css } from 'styled-components'
import { media } from 'styles/media'

interface TextProps {
  uppercase?: boolean
  centered?: boolean
  noMargin?: boolean
  weight?: '500' | '400' | '300'
  styleLink?: boolean
  notResponsive?: boolean
}

const baseTypographyStyles = css<TextProps>`
  ${({ noMargin }) =>
    noMargin &&
    `
    margin-top: 0;
    margin-bottom: 0;
  `}

  ${({ weight }) =>
    weight &&
    `
      font-weight: ${weight};
    `}

    ${media.md<TextProps>`
      text-align: ${({ centered }) => (centered ? 'center' : 'inherit')};
    `}

  ${({ styleLink, theme }) =>
    styleLink &&
    `
    a {
      text-decoration: underline;
      text-underline-offset: 1px;
      text-decoration-thickness: 1px;
      color: ${theme?.['-ds-core-400']?.value};

      &:hover {
        color: ${theme?.['-ds-core-300']?.value};
      }
    }
  `}
`

export const SectionTitle = styled.h3<TextProps>`
  color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  letter-spacing: 0.08em;
  line-height: 1.3;
  text-transform: uppercase;
  margin-bottom: 8px;

  ${baseTypographyStyles}

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 300;
  `}
`

export const HeadingXxl = styled.h4<TextProps>`
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xl']?.['font-size']?.value};
  line-height: 1.3;
  margin-bottom: 24px;

  ${baseTypographyStyles}

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 400;
  `}

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-xxl']?.['font-size']?.value};
  `}
`

export const HeadingXl = styled.h4<TextProps>`
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-lg']?.['font-size']?.value};
  line-height: 1.3;
  margin-bottom: 24px;
  ${baseTypographyStyles}
  ${({ weight }) =>
    !weight &&
    `
    font-weight: 400;
  `}
  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-xl']?.['font-size']?.value};
  `}
`

export const HeadingLg = styled.h4<TextProps>`
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-md']?.['font-size']?.value};
  line-height: 1.3;
  margin-bottom: 8px;

  ${baseTypographyStyles}

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 400;
  `}

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-lg']?.['font-size']?.value};
  `}
`

export const HeadingMd = styled.h4<TextProps>`
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-sm']?.['font-size']?.value};
  line-height: 1.3;
  margin-bottom: 16px;

  ${baseTypographyStyles}

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 400;
  `}

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-md']?.['font-size']?.value};
  `}
`

export const HeadingSm = styled.h4<TextProps>`
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  line-height: 1.3;
  margin-bottom: 16px;

  ${baseTypographyStyles}

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 400;
  `}

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-sm']?.['font-size']?.value};
  `}
`

export const ParagraphLg = styled.p<TextProps>`
  ${baseTypographyStyles}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-sm']?.['font-size']?.value};
  line-height: 1.3;

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 300;
  `}

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-lg']?.['font-size']?.value};
    line-height: 1.4;
  `}
`

export const ParagraphMd = styled.p<TextProps>`
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  line-height: 1.4;

  ${baseTypographyStyles}

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 300;
  `}

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-md']?.['font-size']?.value};
    max-width: 640px;
    margin-left: ${({ centered }) => (centered ? 'auto' : '0')};
    margin-right: ${({ centered }) => (centered ? 'auto' : '0')};
  `}
`

export const ParagraphSm = styled.p<TextProps>`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  line-height: 1.4;
  letter-spacing: 0.01em;

  ${baseTypographyStyles}

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 300;
  `}

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-sm']?.['font-size']?.value};
    line-height: 1.3;
    max-width: 640px;
    margin-left: ${({ centered }) => (centered ? 'auto' : '0')};
    margin-right: ${({ centered }) => (centered ? 'auto' : '0')};
  `}
`

export const ParagraphXs = styled.p<TextProps>`

  font-size: ${({ theme, notResponsive }) =>
    !notResponsive
      ? theme['-ds-font-size-xxs']['font-size'].value
      : theme['-ds-font-size-xs']['font-size'].value};
  color: ${({ theme }) => theme?.['-ds-neutral-600']?.value};
  line-height: 1.4;

  ${baseTypographyStyles}

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 300;
  `}

  ${media.md`
    font-size: ${({ theme }) => theme['-ds-font-size-xs']['font-size'].value};
  `}
`

export const ParagraphXxs = styled.p<TextProps>`
  font-size: ${({ theme }) => theme['-ds-font-size-xxs']['font-size'].value};
  text-align: ${({ centered }) => (centered ? 'center' : 'inherit')};
  text-transform: ${({ uppercase }) => (uppercase ? 'uppercase' : 'none')};
  letter-spacing: 0.01em;

  ${baseTypographyStyles}

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 300;
  `}
`

export const ParagraphMicro = styled.p<TextProps>`
  font-size: ${({ theme }) => theme['-ds-font-size-xxxs']['font-size'].value};
  text-align: ${({ centered }) => (centered ? 'center' : 'inherit')};
  text-transform: ${({ uppercase }) => (uppercase ? 'uppercase' : 'none')};
  letter-spacing: 0.08em;

  ${baseTypographyStyles}

  ${({ weight }) =>
    !weight &&
    `
    font-weight: 400;
  `}
`

export const StyledLink = styled.a`
  text-decoration: underline;
  text-underline-offset: 1px;
  text-decoration-thickness: 1px;
  color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  background-color: transparent;
  border: none;

  &:hover {
    color: ${({ theme }) => theme?.['-ds-core-300']?.value};
  }
`
